import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Seo from "@/components/Seo";
import {
  HandHeart, Plus, MapPin, CalendarDays, Users, X, Check, Loader2,
  HeartHandshake, Trash2, Lock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * /volunteer — Kingdom opportunity board.
 * Admins post needs; members can sign up. Slot-limited posts auto-close when
 * full. Signing up notifies the creator.
 */
export default function Volunteer() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", location: "", starts_at: "", slots: 0, contact: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/volunteer");
      setItems(data);
    } catch {
      toast.error("Could not load opportunities");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        slots: Number(form.slots) || 0,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      };
      await api.post("/volunteer", payload);
      toast.success("Opportunity posted");
      setForm({ title: "", description: "", location: "", starts_at: "", slots: 0, contact: "" });
      setOpenCreate(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally { setBusy(false); }
  };

  const signup = async (op) => {
    try {
      const { data } = await api.post(`/volunteer/${op.id}/signup`);
      setItems((arr) => arr.map((x) => x.id === op.id ? data.opportunity : x));
      toast.success(data.i_signed_up ? "You're signed up — thank you!" : "Removed from list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const close = async (op) => {
    if (!window.confirm(`Close "${op.title}"?`)) return;
    try {
      const { data } = await api.patch(`/volunteer/${op.id}/close`);
      setItems((arr) => arr.map((x) => x.id === op.id ? data : x));
    } catch {}
  };

  const remove = async (op) => {
    if (!window.confirm(`Delete "${op.title}" permanently?`)) return;
    try {
      await api.delete(`/volunteer/${op.id}`);
      setItems((arr) => arr.filter((x) => x.id !== op.id));
    } catch {}
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Seo
        title="Volunteer — Jesus Just Us"
        description="Serve where you're needed. Real opportunities posted by the community — every gift counts toward the kingdom."
      />
      <div className="overline text-burgundy flex items-center gap-2"><HandHeart className="h-3.5 w-3.5" /> Volunteer Center</div>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-jjblue mt-2 leading-tight">Where the family needs your hands.</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)] max-w-xl">
            Vetted opportunities to serve — meals, prayer teams, tech help, mentoring,
            event setup, food drives. Pick what fits, sign up in one click.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setOpenCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="vol-new">
            <Plus className="h-4 w-4" /> Post an opportunity
          </button>
        )}
      </div>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && (
          <div className="col-span-full flex items-center justify-center gap-2 text-[var(--jj-muted)] py-12">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="col-span-full jj-card text-center py-12">
            <HeartHandshake className="h-7 w-7 mx-auto text-burgundy" strokeWidth={1.5} />
            <div className="mt-2 text-jjblue font-serif text-xl">No open opportunities right now.</div>
            <p className="mt-1 text-sm text-[var(--jj-muted)]">Check back soon — the family always needs hands.</p>
          </div>
        )}
        {items.map((op) => {
          const closed = op.status !== "open";
          const full = op.slots > 0 && op.slots_filled >= op.slots;
          return (
            <div key={op.id} className={`jj-card p-5 flex flex-col gap-3 ${closed ? "opacity-70" : ""}`} data-testid={`vol-card-${op.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-2xl text-jjblue leading-tight flex-1">{op.title}</h3>
                {closed && (
                  <span className="text-[10px] uppercase tracking-wider bg-jjblue text-cream px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Closed
                  </span>
                )}
              </div>
              {op.description && <p className="text-sm text-jjblue/85 leading-relaxed">{op.description}</p>}
              <div className="text-xs text-[var(--jj-muted)] grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {op.location && <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {op.location}</div>}
                {op.starts_at && (
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {new Date(op.starts_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {op.slots > 0
                    ? `${op.slots_filled} of ${op.slots} spots filled`
                    : `${op.slots_filled} signed up`}
                </div>
                {op.contact && <div className="truncate">Contact: {op.contact}</div>}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {!closed && (
                  <button
                    onClick={() => signup(op)}
                    disabled={!op.i_signed_up && full}
                    className={`text-sm !py-2 !px-4 inline-flex items-center gap-1.5 ${op.i_signed_up ? "btn-secondary" : "btn-primary"} disabled:opacity-50`}
                    data-testid={`vol-signup-${op.id}`}
                  >
                    {op.i_signed_up ? <><Check className="h-3.5 w-3.5" /> Signed up — undo</> : full ? "Full" : "Count me in"}
                  </button>
                )}
                {isAdmin && (
                  <>
                    {!closed && (
                      <button onClick={() => close(op)} className="btn-secondary !py-2 !px-4 text-sm" data-testid={`vol-close-${op.id}`}>
                        Close
                      </button>
                    )}
                    <button onClick={() => remove(op)} className="text-xs text-[var(--jj-muted)] hover:text-burgundy inline-flex items-center gap-1" data-testid={`vol-delete-${op.id}`}>
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {openCreate && (
        <div className="fixed inset-0 z-50 bg-jjblue/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpenCreate(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="jj-card w-full max-w-lg p-6 sm:p-7"
            data-testid="vol-create-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-burgundy">Post an opportunity</div>
                <h3 className="font-serif text-2xl text-jjblue mt-1">Call the family to serve.</h3>
              </div>
              <button type="button" onClick={() => setOpenCreate(false)} className="text-[var(--jj-muted)] hover:text-burgundy" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">Title</label>
                <input className="input-jj mt-1" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={140} placeholder="e.g., Sunday food bank — packing teams" data-testid="vol-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-burgundy">What's needed</label>
                <textarea className="input-jj mt-1 min-h-[100px]" maxLength={2000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the role, who you're looking for, and what they'll do." data-testid="vol-desc" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">Where</label>
                  <input className="input-jj mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} maxLength={160} placeholder="Church / online / city" data-testid="vol-location" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">When</label>
                  <input type="datetime-local" className="input-jj mt-1" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="vol-when" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">Slots <span className="normal-case text-[var(--jj-muted)]">(0 = unlimited)</span></label>
                  <input type="number" min="0" max="10000" className="input-jj mt-1" value={form.slots} onChange={(e) => setForm({ ...form, slots: e.target.value })} data-testid="vol-slots" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-burgundy">Contact</label>
                  <input className="input-jj mt-1" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} maxLength={200} placeholder="Name or email" data-testid="vol-contact" />
                </div>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full mt-5" data-testid="vol-submit">
              {busy ? "Posting…" : "Post opportunity"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
